import React, { useMemo } from 'react';
import mergeDeepRight from 'ramda/src/mergeDeepRight';
import { gql, useQuery } from '@apollo/client';
import { LegacyPermissionsQuery } from '#types/generated/graphql';

import PermissionsContext from '../contexts/PermissionsContext';
import Loading from '#coreComponents/Loading';
import CommonPermissions from './CommonPermissions';
import { useInstitution } from '#core/Institution';

const LegacyPermissions: React.FC<{}> = ({ children }) => {
  const { institution } = useInstitution();

  const { loading, error, data } = useQuery<LegacyPermissionsQuery>(
    gql`
      query LegacyPermissions($institutionId: String!) {
        legacyPermissions(institutionId: $institutionId) {
          resource
          actions {
            action
            grant
          }
        }
      }
    `,
    {
      variables: {
        institutionId: institution.institutionId,
      },
    },
  );

  const legacyResourcePermissions = useMemo(
    () =>
      (data?.legacyPermissions || []).reduce((perms, permission) => {
        if (!permission) {
          return perms;
        }
        const { resource, actions } = permission;
        return {
          ...perms,
          [resource]: actions.reduce((a, resourceActions) => {
            if (!resourceActions) {
              return a;
            }
            const { action, grant } = resourceActions;
            return {
              ...a,
              [action]: grant,
            };
          }, {} as Record<string, boolean>),
        };
      }, {} as Record<string, Record<string, boolean>>),
    [data?.legacyPermissions],
  );

  if (loading) {
    return <Loading width="100%" height="100vh" />;
  }

  if (error) {
    throw error;
  }

  return (
    <PermissionsContext.Consumer>
      {({ resources }) => (
        <CommonPermissions
          resourcePermissions={mergeDeepRight(
            resources,
            legacyResourcePermissions,
          )}
        >
          {children}
        </CommonPermissions>
      )}
    </PermissionsContext.Consumer>
  );
};

export default LegacyPermissions;
